import { PanelLeft } from 'lucide-react';
import type React from 'react';
import { useTranslation } from 'react-i18next';
import { useAppContext } from '@/app/providers/AppContextProvider';
import { Button } from '@/components/ui/button';
import { getStatusLabel } from '@/lib/quoteStatus';
import { cn } from '@/lib/utils';
import SortButton from './SortButton';

interface Customer {
	company_name?: string;
}

interface SavingPercentages {
	max: number;
}

interface HeadingProps {
	customer?: Customer;
	savingPercentages: SavingPercentages;
	loading?: boolean;
	status?: string;
	expectedNumQuotes: number;
	quotesReady: number;
	onSortChange: (sortKey: string) => void;
	currentSort: string;
}

const Heading = ({
	customer,
	savingPercentages,
	loading = true,
	status = 'loading',
	expectedNumQuotes,
	quotesReady,
	onSortChange,
	currentSort
}: HeadingProps): React.JSX.Element => {
	const { t } = useTranslation();
	const { setIsMobileSidebarOpen } = useAppContext();

	const companyName = customer?.company_name
		? customer.company_name.length > 80
			? `${customer.company_name.slice(0, 80)}...`
			: customer.company_name
		: t('quotes.yourCompany');
	const savingSummary =
		savingPercentages.max <= 0
			? ''
			: t('quotes.canSave', { percentage: savingPercentages.max.toFixed(0) });

	// 'loading' is a UI-only pseudo-status; every real backend status resolves via
	// the shared status-label map (with its status.json description fallback).
	const subtitle = loading
		? status === 'loading'
			? t('quotes.status.loading')
			: getStatusLabel(t, status)
		: null;

	return (
		<>
			<div className="flex items-start justify-between gap-3">
				<h1 className="text-4xl font-bold py-1 pb-2 leading-snug">{t('quotes.title')}</h1>
				{/* Mobile-only trigger for the "your details" side panel, which is
				    always visible on desktop but collapses to a slide-over below xl. */}
				<Button
					type="button"
					variant="outline"
					size="sm"
					onClick={() => setIsMobileSidebarOpen(true)}
					className="xl:hidden shrink-0 px-2 mt-1"
					aria-label={t('header.openDetails')}
					data-testid="header-open-details"
				>
					<PanelLeft className="w-4 h-4" aria-hidden />
				</Button>
			</div>
			<div className="flex justify-between items-center">
				<div>
					<h2 className={cn('text-lg font-bold text-primary mb-0', loading && 'animate-pulse')}>
						{loading ? (
							<span>{subtitle}</span>
						) : (
							<div className="flex flex-col sm:flex-row sm:items-center gap-0 sm:gap-1">
								<div className="whitespace-nowrap">{t('quotes.offerFor', { companyName })}</div>
								{savingSummary && <div className="whitespace-nowrap">{savingSummary}</div>}
							</div>
						)}
						{loading && quotesReady > 0 && (
							<span className="text-primary opacity-75 text-sm ml-1">
								({quotesReady}/{expectedNumQuotes})
							</span>
						)}
					</h2>
				</div>
				{!loading && quotesReady > 1 && (
					<SortButton onSortChange={onSortChange} currentSort={currentSort} />
				)}
			</div>
		</>
	);
};

export default Heading;
